import { Link } from "react-router-dom";
import { MY_PROFILE } from "@/_constant";
import { useLanguageContext } from "@/hooks";
import { resolveDashboardStatus } from "./dashboard-profile-card.resolver";
import type { DashboardAction, DashboardCardConfig, DashboardProfile } from "./types";
import { Button } from "@/_component/Common";

interface Props {
  selected: { profile: DashboardProfile; config: DashboardCardConfig };
  items: Array<{ profile: DashboardProfile; config: DashboardCardConfig }>;
  agreementSigned: boolean | null;
  onSelect: (categoryId: string) => void;
  executeAction: (action: DashboardAction) => void;
}

const ProfileCardHeader = ({ selected, items, agreementSigned, onSelect, executeAction }: Props) => {
  const { language } = useLanguageContext();
  const { profile, config } = selected;
  const status = config.showStatus ? resolveDashboardStatus(profile, config, agreementSigned) : null;
  const displayName = (item: typeof selected) => {
    const key = item.config.displayCode || item.profile.displayCode;
    return language(key) || item.profile.categoryName || item.profile.accessName;
  };
  const statusClass = status?.tone === "success" ? "text-success" : status?.tone === "danger" ? "text-danger" : "text-warning";

  return (
    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
      <div className="d-flex align-items-center">
        <Link to={MY_PROFILE} className="me-3 position-relative">
          <div className="avatar-border rounded-circle bg-white d-flex align-items-center justify-content-center" style={{ width: 42, height: 42 }}>
            <i className="bi bi-person kg-purple fs-5" />
          </div>
        </Link>
        <div>
          <div className="dropdown">
            <button type="button" className="btn border-0 p-0 fw-bold kg-dark" data-bs-toggle="dropdown" aria-expanded="false">
              {displayName(selected)}
              <i className="bi bi-caret-down-fill ms-2 text-muted small" />
            </button>
            <ul className="dropdown-menu shadow border-0 mt-2 rounded-16">
              {items.map((item) => (
                <li key={item.profile.categoryId}>
                  <button type="button" className="dropdown-item animate-all-ease-in-out py-2 px-3 small fw-bold" onClick={() => onSelect(item.profile.categoryId)}>
                    {displayName(item)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="d-flex align-items-center mt-1">
            {config.showRating && (
              <span className="text-success me-2 fw-bold small"><i className="bi bi-star-fill me-1" />{profile?.rating ?? 0}</span>
            )}
            {config.showPostsRemaining && (
              <span className="text-muted small border-start border-end px-2">{profile?.postsRemaining ?? 0} posts remaining</span>
            )}
            {status && (
              <button type="button" className={` ms-2 btn btn-sm bg-white border rounded-pill px-3 ${statusClass}`} disabled={!status.action} onClick={() => status.action && executeAction(status.action)}>
                {language(status.labelKey) || status.labelKey}
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        {config.showPlan && profile.planName && (
          <>
            <div className="badge rounded-pill bg-white border d-flex align-items-center p-2 fw-normal fs-8">
              <i className="bi bi-crown-fill me-2 text-secondary" />
              <span className="text-secondary">{profile.planName}{profile.planDaysRemaining !== null && `: Expires in ${profile.planDaysRemaining} days`}</span>
            </div>
          </>
        )}
        {config.showMis && (
          <Button white outline small rounded onClick={() => config.misAction && executeAction(config.misAction)}>
              <i className="bi bi-file-earmark-bar-graph me-2"></i>
              View MIS report
            </Button>
        )}
      </div>
    </div>
  );
};

export default ProfileCardHeader;
